import {
	type ChangeEvent,
	type FormEvent,
	type ReactNode,
	useEffect,
	useId,
	useMemo,
	useRef,
	useState,
} from 'react';

import { css } from '@compiled/react';
import { flattenValues } from '@uniformdev/canvas';
import { type IntlShape, useIntl } from 'react-intl';

import { useAnalyticsEvents } from '@atlaskit/analytics-next';
import { media } from '@atlaskit/primitives/responsive';
import { token } from '@atlaskit/tokens';

import {
	ConsentHubClientConfigProvider,
	updatePreferences,
	useConsentHubClientConfig,
} from '@/analytics/consent-hub/consent-client';
import { useBxpWebStargateClient } from '@/libraries/bxp-web-stargate-client/src/hook/use-bxp-web-stargate-client';
import { Button, type ButtonVariant } from '@/libraries/design/components/button/src/button';
import { wacToken } from '@/libraries/design/foundations';
import { useLocalizedHref } from '@/libraries/web-framework/src/localization/use-localized-href';
import { TypographyStyles } from '@/libraries/wpl/foundations/src/typography-styles';
import { isProduction } from '@/utils/urls';

import { useWAEContext } from '../../../utils/wae-context-provider';

import { getWebinarUtmAttribution } from './webinar-form-attribution';
import { webinarFormMessages } from './webinar-form-messages';
import { getWebinarCountries, WEBINAR_COMPANY_SIZES } from './webinar-form-options';
import {
	type WebinarFormErrors,
	type WebinarFormField,
	type WebinarFormValues,
	validateWebinarForm,
} from './webinar-form-validation';

const WEBINAR_ENDPOINT = '/endpoint/webinarsignup/bigmarker';
const STAGING_WAC_ORIGIN = 'https://wac.stg.internal.atlassian.com';
const STAGING_WAC_HOSTNAMES = new Set([
	'127.0.0.1',
	'localhost',
	'wac-web.stg-east.frontend.public.atl-paas.net',
]);
const REGISTRATION_TIMEOUT_MS = 15_000;
const CONSENT_TIMEOUT_MS = 5_000;
const MAX_TIMEOUT_MS = 2_147_483_647;
const DEFAULT_ANALYTICS_LABEL = 'Webinar form';

export type WebinarSession = {
	eventId: string;
	dateLabel?: string;
	timeCityLabel?: string;
	date?: string | UniformDateTimeValue;
	timezoneCity?: string;
};

export type UniformDateTimeValue = {
	datetime: string;
	timeZone?: string;
};

type FormAlignment = 'start' | 'center';
type WebinarButtonVariant = Exclude<ButtonVariant, 'secondary'>;

export type UniformWebinarFormProps = {
	label?: string;
	onDemandConversionDateTime: string | UniformDateTimeValue;
	showPhone?: boolean;
	blockPersonalEmails?: boolean;
	alignment?: FormAlignment;
	buttonVariant?: WebinarButtonVariant;
	sessions: WebinarSession[];
};

type WebinarMode = 'scheduled' | 'on-demand';

const EMPTY_VALUES: WebinarFormValues = {
	firstName: '',
	lastName: '',
	company: '',
	department: '',
	jobTitle: '',
	email: '',
	phone: '',
	companySize: '',
	country: '',
	eventId: '',
	alternateEmail: '',
	marketingOptIn: false,
};

const formCss = css({
	color: wacToken('color.primary.black'),
	fontFamily: wacToken('font.charlie.text'),
	width: '100%',
});

const headingCss = css({
	margin: `0 0 ${token('space.300')}`,
	'&.bold': {
		fontWeight: 700,
	},
});

const fieldsCss = css({
	display: 'grid',
	gap: token('space.200'),
	gridTemplateColumns: '1fr',
	[media.above.md]: {
		gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
	},
});

const fieldCss = css({
	display: 'flex',
	flexDirection: 'column',
	gap: token('space.050'),
	minWidth: 0,
});

const fullWidthFieldCss = css({
	[media.above.md]: {
		gridColumn: '1 / -1',
	},
});

const labelCss = css({
	fontSize: '0.875rem',
	fontWeight: 500,
	lineHeight: '1.25rem',
});

const requiredIndicatorCss = css({
	color: wacToken('color.secondary.red'),
	display: 'inline',
	paddingInlineStart: token('space.025'),
	verticalAlign: 'baseline',
});

const controlCss = css({
	appearance: 'none',
	backgroundColor: wacToken('color.neutral.light0'),
	border: `1px solid ${wacToken('color.neutral.light600')}`,
	borderRadius: wacToken('corner.radius.card'),
	boxSizing: 'border-box',
	color: wacToken('color.primary.black'),
	display: 'block',
	fontFamily: wacToken('font.charlie.text'),
	fontSize: '1rem',
	fontWeight: 400,
	lineHeight: '1.5rem',
	margin: 0,
	maxWidth: '100%',
	minHeight: token('space.500'),
	minWidth: 0,
	padding: `0 ${token('space.150')}`,
	width: '100%',
	'&:focus': {
		borderColor: wacToken('color.primary.blue'),
		boxShadow: `inset 0 0 0 1px ${wacToken('color.primary.blue')}`,
		outline: 'none',
	},
	'&[aria-invalid="true"]': {
		borderColor: wacToken('color.secondary.red'),
		boxShadow: `inset 0 0 0 1px ${wacToken('color.secondary.red')}`,
	},
});

const selectCss = css({
	backgroundImage:
		"url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='9' viewBox='0 0 14 9' fill='none'%3E%3Cpath fill-rule='evenodd' clip-rule='evenodd' d='M0.282354 2.04788C-0.0941681 1.65992 -0.0941683 1.04295 0.282353 0.654992L0.623427 0.303554C1.01623 -0.101184 1.66584 -0.101184 2.05864 0.303554L6.60645 4.98954L11.1542 0.303554C11.547 -0.101184 12.1967 -0.101184 12.5895 0.303554L12.9305 0.654992C13.3071 1.04295 13.3071 1.65992 12.9305 2.04788L7.32405 7.82473C6.93125 8.22946 6.28164 8.22946 5.88884 7.82473L0.282354 2.04788Z' fill='%23101214'/%3E%3C/svg%3E\")",
	backgroundPosition: `right ${token('space.150')} center`,
	backgroundRepeat: 'no-repeat',
	backgroundSize: '14px 9px',
	paddingInlineEnd: token('space.400'),
});

const errorCss = css({
	color: wacToken('color.secondary.red'),
	fontSize: '0.75rem',
	fontWeight: 400,
	lineHeight: '1.125rem',
});

const sessionFieldsetCss = css({
	border: 0,
	margin: `0 0 ${token('space.300')}`,
	padding: 0,
});

const legendCss = css({
	fontSize: '1rem',
	fontWeight: 600,
	marginBlockEnd: token('space.150'),
	padding: 0,
});

const sessionOptionCss = css({
	alignItems: 'flex-start',
	cursor: 'pointer',
	display: 'flex',
	gap: token('space.100'),
	marginBlockEnd: token('space.100'),
});

const radioCss = css({
	accentColor: wacToken('color.primary.blue'),
	height: token('space.250'),
	margin: `${token('space.025')} 0 0`,
	width: token('space.250'),
});

const singleSessionCss = css({
	fontWeight: 600,
	margin: `0 0 ${token('space.300')}`,
});

const disclosureCss = css({
	fontSize: '0.875rem',
	lineHeight: '1.25rem',
	margin: `${token('space.300')} 0 ${token('space.200')}`,
});

const checkboxLabelCss = css({
	alignItems: 'flex-start',
	display: 'flex',
	fontSize: '0.875rem',
	gap: token('space.100'),
	lineHeight: '1.25rem',
	marginBlockEnd: token('space.200'),
});

const checkboxCss = css({
	accentColor: wacToken('color.primary.blue'),
	flex: '0 0 auto',
	height: token('space.200'),
	margin: `${token('space.025')} 0 0`,
	width: token('space.200'),
});

const privacyLinkCss = css({
	color: wacToken('color.primary.blue'),
	marginInlineStart: token('space.050'),
});

const alertCss = css({
	color: wacToken('color.secondary.red'),
	marginBlock: token('space.200'),
});

const visuallyHiddenCss = css({
	height: '1px',
	insetInlineStart: '-10000px',
	overflow: 'hidden',
	position: 'absolute',
	width: '1px',
});

type FieldShellProps = {
	id: string;
	label: string;
	error?: string;
	fullWidth?: boolean;
	children: ReactNode;
};

const RequiredIndicator = () => (
	<span aria-hidden="true" css={requiredIndicatorCss}>
		*
	</span>
);

const FieldShell = ({ id, label, error, fullWidth = false, children }: FieldShellProps) => (
	<div css={[fieldCss, fullWidth && fullWidthFieldCss]}>
		<label css={labelCss} htmlFor={id}>
			{label}
			<RequiredIndicator />
		</label>
		{children}
		{error && (
			<span css={errorCss} id={`${id}-error`}>
				{error}
			</span>
		)}
	</div>
);

type TextFieldProps = {
	id: string;
	field: WebinarFormField;
	label: string;
	value: string;
	error?: string;
	autoComplete?: string;
	fullWidth?: boolean;
	type?: 'text' | 'email' | 'tel';
	onChange: (event: ChangeEvent<HTMLInputElement>) => void;
};

const TextField = ({
	id,
	field,
	label,
	value,
	error,
	autoComplete,
	fullWidth = false,
	type = 'text',
	onChange,
}: TextFieldProps) => (
	<FieldShell id={id} label={label} error={error} fullWidth={fullWidth}>
		<input
			aria-describedby={error ? `${id}-error` : undefined}
			aria-invalid={Boolean(error)}
			autoComplete={autoComplete}
			css={controlCss}
			data-field={field}
			id={id}
			name={field}
			onChange={onChange}
			required
			type={type}
			value={value}
		/>
	</FieldShell>
);

const getConversionTime = (
	onDemandConversionDateTime: UniformWebinarFormProps['onDemandConversionDateTime'],
): number | undefined => {
	const dateTime =
		typeof onDemandConversionDateTime === 'string'
			? onDemandConversionDateTime
			: onDemandConversionDateTime?.datetime;
	if (typeof dateTime !== 'string') {
		return undefined;
	}

	const conversionTime = Date.parse(dateTime);
	return Number.isNaN(conversionTime) ? undefined : conversionTime;
};

type StructuredSessionDate = {
	dateLabel: string;
	timeLabel: string;
};

const parseWallClockDate = (dateTime: string): Date | undefined => {
	const match =
		/^(?<year>\d{4})-(?<month>\d{2})-(?<day>\d{2})T(?<hour>\d{2}):(?<minute>\d{2})(?::(?<second>\d{2}))?/.exec(
			dateTime,
		);
	if (!match?.groups) {
		return undefined;
	}

	const { year, month, day, hour, minute, second = '0' } = match.groups;
	const date = new Date(
		Number(year),
		Number(month) - 1,
		Number(day),
		Number(hour),
		Number(minute),
		Number(second),
	);
	const hasMatchingComponents =
		date.getFullYear() === Number(year) &&
		date.getMonth() === Number(month) - 1 &&
		date.getDate() === Number(day) &&
		date.getHours() === Number(hour) &&
		date.getMinutes() === Number(minute) &&
		date.getSeconds() === Number(second);

	return hasMatchingComponents ? date : undefined;
};

const formatSessionDate = (
	date: Date,
	formatDate: IntlShape['formatDate'],
): StructuredSessionDate => {
	const dateOptions = {
		day: 'numeric',
		month: 'long',
		year: 'numeric',
	} as const;
	const timeOptions = {
		hour: 'numeric',
		minute: '2-digit',
	} as const;
	return {
		dateLabel: formatDate(date, dateOptions),
		timeLabel: formatDate(date, timeOptions),
	};
};

const getStructuredSessionDate = (
	session: WebinarSession,
	formatDate: IntlShape['formatDate'],
): StructuredSessionDate | undefined => {
	const structuredDateTime =
		typeof session.date === 'string' ? session.date : session.date?.datetime;
	if (!structuredDateTime) {
		return undefined;
	}

	const parsedDate = parseWallClockDate(structuredDateTime);
	if (!parsedDate) {
		return undefined;
	}

	return formatSessionDate(parsedDate, formatDate);
};

const getSessionDisplayLabel = (
	session: WebinarSession,
	formatDate: IntlShape['formatDate'],
): string => {
	const structuredDate = getStructuredSessionDate(session, formatDate);
	const timezoneCity = session.timezoneCity?.trim();
	if (structuredDate) {
		const timeLabel = timezoneCity
			? `${structuredDate.timeLabel} ${timezoneCity}`
			: session.timeCityLabel?.trim() || structuredDate.timeLabel;
		return `${structuredDate.dateLabel} — ${timeLabel}`;
	}

	const dateLabel = session.dateLabel?.trim() ?? '';
	const timeCityLabel = timezoneCity || session.timeCityLabel?.trim();
	return `${dateLabel}${dateLabel && timeCityLabel ? ` — ${timeCityLabel}` : ''}`;
};

const getWebinarEndpoint = (): string => {
	if (typeof window === 'undefined') {
		return WEBINAR_ENDPOINT;
	}

	const hostname = window.location.hostname.toLowerCase();
	return STAGING_WAC_HOSTNAMES.has(hostname)
		? `${STAGING_WAC_ORIGIN}${WEBINAR_ENDPOINT}`
		: WEBINAR_ENDPOINT;
};

const withTimeout = async <T,>(promise: Promise<T>, timeoutMs: number): Promise<T> => {
	let timeoutId: ReturnType<typeof setTimeout> | undefined;
	const timeoutPromise = new Promise<never>((_, reject) => {
		timeoutId = setTimeout(() => reject(new Error('Request timed out')), timeoutMs);
	});

	try {
		return await Promise.race([promise, timeoutPromise]);
	} finally {
		if (timeoutId) {
			clearTimeout(timeoutId);
		}
	}
};

const postRegistration = async (payload: Record<string, unknown>) => {
	const controller = new AbortController();
	const timeoutId = setTimeout(() => controller.abort(), REGISTRATION_TIMEOUT_MS);

	try {
		const response = await fetch(getWebinarEndpoint(), {
			body: JSON.stringify(payload),
			headers: { 'Content-Type': 'application/json' },
			method: 'POST',
			signal: controller.signal,
		});

		if (!response.ok) {
			throw new Error(`Webinar registration failed with status ${response.status}`);
		}

		const data = (await response.json()) as { conference_url?: unknown };
		if (typeof data.conference_url !== 'string' || !data.conference_url) {
			throw new Error('Webinar registration response did not include a conference URL');
		}
		return data.conference_url;
	} finally {
		clearTimeout(timeoutId);
	}
};

const WebinarFormContent = ({
	label,
	onDemandConversionDateTime,
	showPhone = false,
	blockPersonalEmails = false,
	alignment = 'start',
	buttonVariant = 'primary',
	sessions = [],
}: // eslint-disable-next-line complexity -- Conditional fields, session modes, and consent states compose one form.
UniformWebinarFormProps) => {
	const intl = useIntl();
	const { locale: waeLocale } = useWAEContext();
	const locale = waeLocale ?? intl.locale;
	const language = locale.toLowerCase().split(/[-_]/)[0] ?? 'en';
	const isJapanese = language === 'ja';
	const privacyPolicyHref = useLocalizedHref('/legal/privacy-policy');
	const stargateClient = useBxpWebStargateClient();
	const consentConfig = useConsentHubClientConfig();
	const { createAnalyticsEvent } = useAnalyticsEvents();
	const formRef = useRef<HTMLFormElement>(null);
	const serverErrorRef = useRef<HTMLDivElement>(null);
	const submitInFlightRef = useRef(false);
	const idPrefix = useId().replace(/:/g, '');
	const [mode, setMode] = useState<WebinarMode>();
	const [hasInitialized, setHasInitialized] = useState(false);
	const [values, setValues] = useState(EMPTY_VALUES);
	const [errors, setErrors] = useState<WebinarFormErrors>({});
	const [serverError, setServerError] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);

	const { flattenedSessions, hasSessionWithoutDate } = useMemo(() => {
		const flattened =
			(flattenValues(sessions as never) as unknown as WebinarSession[] | null) ?? [];
		const normalized = flattened.map(
			(session, index) => session ?? (sessions[index] as WebinarSession | undefined),
		);
		const withDisplayLabels = normalized.map((session) =>
			session
				? { ...session, displayLabel: getSessionDisplayLabel(session, intl.formatDate) }
				: undefined,
		);
		return {
			flattenedSessions: withDisplayLabels.filter(
				(session): session is WebinarSession & { displayLabel: string } =>
					typeof session?.eventId === 'string' &&
					Boolean(session.eventId.trim()) &&
					Boolean(session.displayLabel),
			),
			hasSessionWithoutDate: withDisplayLabels.some(
				(session) =>
					typeof session?.eventId === 'string' &&
					Boolean(session.eventId.trim()) &&
					!session.displayLabel,
			),
		};
	}, [intl.formatDate, sessions]);
	const hasMalformedConfiguration =
		flattenedSessions.length === 0 || flattenedSessions.length !== sessions.length;
	const hasMultipleSessions = mode === 'scheduled' && flattenedSessions.length > 1;
	const selectedSession =
		flattenedSessions.find((session) => session.eventId === values.eventId) ?? flattenedSessions[0];
	const analyticsLabel = label?.trim() || DEFAULT_ANALYTICS_LABEL;
	const countries = useMemo(() => getWebinarCountries(isJapanese), [isJapanese]);
	const alignedContentCss = css({ textAlign: alignment });

	const conversionTime = getConversionTime(onDemandConversionDateTime);

	useEffect(() => {
		let timeoutId: ReturnType<typeof setTimeout> | undefined;

		const updateMode = () => {
			if (conversionTime === undefined) {
				setMode(undefined);
				return;
			}

			const remainingTime = conversionTime - Date.now();
			if (remainingTime <= 0) {
				setMode('on-demand');
				return;
			}

			setMode('scheduled');
			timeoutId = setTimeout(updateMode, Math.min(remainingTime, MAX_TIMEOUT_MS));
		};

		updateMode();
		setHasInitialized(true);

		return () => {
			if (timeoutId) {
				clearTimeout(timeoutId);
			}
		};
	}, [conversionTime]);

	useEffect(() => {
		const firstEventId = flattenedSessions[0]?.eventId ?? '';
		setValues((current) =>
			current.eventId === firstEventId ? current : { ...current, eventId: firstEventId },
		);
	}, [flattenedSessions]);

	const getId = (field: string) => `${idPrefix}-webinar-${field}`;
	const clearFieldError = (field: WebinarFormField) => {
		setErrors((current) => {
			if (!current[field]) {
				return current;
			}
			const next = { ...current };
			delete next[field];
			return next;
		});
	};
	const updateTextValue = (field: WebinarFormField) => (event: ChangeEvent<HTMLInputElement>) => {
		setValues((current) => ({ ...current, [field]: event.target.value }));
		clearFieldError(field);
	};
	const updateSelectValue =
		(field: WebinarFormField) => (event: ChangeEvent<HTMLSelectElement>) => {
			setValues((current) => ({ ...current, [field]: event.target.value }));
			clearFieldError(field);
		};
	const focusFirstError = (validationErrors: WebinarFormErrors) => {
		const firstError = Object.keys(validationErrors)[0] as WebinarFormField | undefined;
		if (firstError) {
			requestAnimationFrame(() => {
				formRef.current?.querySelector<HTMLElement>(`[data-field="${firstError}"]`)?.focus();
			});
		}
	};
	const fireTerminalAnalytics = (
		status: 'success' | 'error',
		eventId: string,
		currentMode: WebinarMode,
	) => {
		createAnalyticsEvent({
			eventType: 'ui',
			action: 'submitted',
			actionSubject: 'form',
			actionSubjectId: 'webinarForm',
			source: 'uniform',
			attributes: {
				label: analyticsLabel,
				status,
				eventId,
				mode: currentMode,
				sessionCount: flattenedSessions.length,
			},
		}).fire();
	};

	const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		if (submitInFlightRef.current || !mode || !selectedSession || hasMalformedConfiguration) {
			return;
		}

		if (values.alternateEmail) {
			return;
		}

		const validationErrors = validateWebinarForm(values, {
			blockPersonalEmails,
			hasMultipleSessions,
			isJapanese,
			messages: {
				businessEmail: (domain) =>
					intl.formatMessage(webinarFormMessages.businessEmail, { domain }),
				invalidEmail: intl.formatMessage(webinarFormMessages.invalidEmail),
				required: intl.formatMessage(webinarFormMessages.required),
			},
			showPhone,
		});
		setErrors(validationErrors);
		if (Object.keys(validationErrors).length) {
			focusFirstError(validationErrors);
			return;
		}

		submitInFlightRef.current = true;
		setIsSubmitting(true);
		setServerError(false);

		const eventId = selectedSession.eventId;
		const payload = {
			eventId,
			email: values.email.trim(),
			firstName: values.firstName.trim(),
			lastName: values.lastName.trim(),
			job_title: values.jobTitle.trim(),
			country: values.country,
			company_size: values.companySize,
			currLanguage: language,
			addCompanyName: true,
			addPhoneNumber: showPhone,
			...(isJapanese ? { department: values.department.trim() } : {}),
			company: values.company.trim(),
			...(showPhone ? { phone: values.phone.trim() } : {}),
			...getWebinarUtmAttribution(),
		};
		const privacyPolicyText = intl.formatMessage(webinarFormMessages.privacyDisclosure);
		const consentLabelText = intl.formatMessage(webinarFormMessages.marketingOptIn);
		const consentPromise = withTimeout(
			updatePreferences(
				stargateClient as unknown as Parameters<typeof updatePreferences>[0],
				{
					consentCheckboxValue: values.marketingOptIn,
					consentLabelText,
					email: values.email.trim(),
					formId: 'webinarForm',
					formUrl: window.location.href,
					locale,
					paramsLocale: locale,
					privacyPolicyText,
					site: 'atlassian',
					source: 'uniform',
				},
				consentConfig,
			),
			CONSENT_TIMEOUT_MS,
		).catch(() => undefined);

		try {
			const [conferenceUrl] = await Promise.all([postRegistration(payload), consentPromise]);
			fireTerminalAnalytics('success', eventId, mode);
			if (isJapanese) {
				const analyticsWindow = window as Window & {
					dataLayer?: Array<Record<string, unknown>>;
				};
				analyticsWindow.dataLayer = analyticsWindow.dataLayer ?? [];
				analyticsWindow.dataLayer.push({ event: 'JapanWebinarRegistration', id: eventId });
			}
			window.location.assign(conferenceUrl);
		} catch {
			setServerError(true);
			fireTerminalAnalytics('error', eventId, mode);
			requestAnimationFrame(() => serverErrorRef.current?.focus());
		} finally {
			submitInFlightRef.current = false;
			setIsSubmitting(false);
		}
	};

	if (!hasInitialized) {
		return <p aria-live="polite">{intl.formatMessage(webinarFormMessages.initializing)}</p>;
	}

	if (!mode || hasMalformedConfiguration || !selectedSession) {
		if (isProduction()) {
			return null;
		}
		return (
			<p role="alert">
				{intl.formatMessage(
					hasSessionWithoutDate
						? webinarFormMessages.sessionDateConfigurationError
						: webinarFormMessages.configurationError,
				)}
			</p>
		);
	}

	const heading =
		mode === 'on-demand'
			? intl.formatMessage(webinarFormMessages.onDemandHeading)
			: intl.formatMessage(webinarFormMessages.registerHeading);
	const submitLabel =
		mode === 'on-demand'
			? intl.formatMessage(webinarFormMessages.watchButton)
			: intl.formatMessage(webinarFormMessages.registerButton);
	const showMarketingOptIn = Boolean(consentConfig.localeRequiresMarketingCommunicationOptIn);

	return (
		<TypographyStyles>
			<section css={formCss} data-testid="uniform-webinar-form">
				<h2 className="body-md bold" css={[headingCss, alignedContentCss]}>
					{heading}
				</h2>
				{mode === 'scheduled' && flattenedSessions.length === 1 && (
					<p css={[singleSessionCss, alignedContentCss]}>{selectedSession.displayLabel}</p>
				)}
				<form autoComplete="on" noValidate onSubmit={handleSubmit} ref={formRef}>
					{hasMultipleSessions && (
						<fieldset
							aria-describedby={errors.eventId ? `${getId('eventId')}-error` : undefined}
							css={sessionFieldsetCss}
						>
							<legend css={legendCss}>
								{intl.formatMessage(webinarFormMessages.sessionLegend)}
								<RequiredIndicator />
							</legend>
							{flattenedSessions.map((session) => (
								<label css={sessionOptionCss} key={session.eventId}>
									<input
										checked={values.eventId === session.eventId}
										css={radioCss}
										data-field="eventId"
										name="eventId"
										onChange={() => {
											setValues((current) => ({ ...current, eventId: session.eventId }));
											clearFieldError('eventId');
										}}
										required
										type="radio"
										value={session.eventId}
									/>
									<span>{session.displayLabel}</span>
								</label>
							))}
							{errors.eventId && (
								<span css={errorCss} id={`${getId('eventId')}-error`}>
									{errors.eventId}
								</span>
							)}
						</fieldset>
					)}
					<div css={fieldsCss} data-testid="webinar-form-fields">
						{isJapanese ? (
							<>
								<TextField
									autoComplete="family-name"
									error={errors.lastName}
									field="lastName"
									id={getId('lastName')}
									label={intl.formatMessage(webinarFormMessages.lastName)}
									onChange={updateTextValue('lastName')}
									value={values.lastName}
								/>
								<TextField
									autoComplete="given-name"
									error={errors.firstName}
									field="firstName"
									id={getId('firstName')}
									label={intl.formatMessage(webinarFormMessages.firstName)}
									onChange={updateTextValue('firstName')}
									value={values.firstName}
								/>
							</>
						) : (
							<>
								<TextField
									autoComplete="given-name"
									error={errors.firstName}
									field="firstName"
									id={getId('firstName')}
									label={intl.formatMessage(webinarFormMessages.firstName)}
									onChange={updateTextValue('firstName')}
									value={values.firstName}
								/>
								<TextField
									autoComplete="family-name"
									error={errors.lastName}
									field="lastName"
									id={getId('lastName')}
									label={intl.formatMessage(webinarFormMessages.lastName)}
									onChange={updateTextValue('lastName')}
									value={values.lastName}
								/>
							</>
						)}
						<TextField
							autoComplete="email"
							error={errors.email}
							field="email"
							fullWidth
							id={getId('email')}
							label={intl.formatMessage(webinarFormMessages.email)}
							onChange={updateTextValue('email')}
							type="email"
							value={values.email}
						/>
						<TextField
							autoComplete="organization"
							error={errors.company}
							field="company"
							id={getId('company')}
							label={intl.formatMessage(webinarFormMessages.company)}
							onChange={updateTextValue('company')}
							value={values.company}
						/>
						{isJapanese && (
							<TextField
								autoComplete="organization-title"
								error={errors.department}
								field="department"
								id={getId('department')}
								label={intl.formatMessage(webinarFormMessages.department)}
								onChange={updateTextValue('department')}
								value={values.department}
							/>
						)}
						<TextField
							autoComplete="organization-title"
							error={errors.jobTitle}
							field="jobTitle"
							id={getId('jobTitle')}
							label={intl.formatMessage(webinarFormMessages.jobTitle)}
							onChange={updateTextValue('jobTitle')}
							value={values.jobTitle}
						/>
						<FieldShell
							error={errors.companySize}
							id={getId('companySize')}
							label={intl.formatMessage(webinarFormMessages.companySize)}
						>
							<select
								aria-describedby={errors.companySize ? `${getId('companySize')}-error` : undefined}
								aria-invalid={Boolean(errors.companySize)}
								css={[controlCss, selectCss]}
								data-field="companySize"
								id={getId('companySize')}
								name="company_size"
								onChange={updateSelectValue('companySize')}
								required
								value={values.companySize}
							>
								<option disabled value="">
									{intl.formatMessage(webinarFormMessages.selectOption)}
								</option>
								{WEBINAR_COMPANY_SIZES.map((companySize) => (
									<option key={companySize} value={companySize}>
										{companySize}
									</option>
								))}
							</select>
						</FieldShell>
						<FieldShell
							error={errors.country}
							id={getId('country')}
							label={intl.formatMessage(webinarFormMessages.country)}
						>
							<select
								aria-describedby={errors.country ? `${getId('country')}-error` : undefined}
								aria-invalid={Boolean(errors.country)}
								autoComplete="country-name"
								css={[controlCss, selectCss]}
								data-field="country"
								id={getId('country')}
								name="country"
								onChange={updateSelectValue('country')}
								required
								value={values.country}
							>
								<option disabled value="">
									{intl.formatMessage(webinarFormMessages.selectOption)}
								</option>
								{countries.map((country) => (
									<option key={country} value={country}>
										{country}
									</option>
								))}
							</select>
						</FieldShell>
						{showPhone && (
							<TextField
								autoComplete="tel"
								error={errors.phone}
								field="phone"
								id={getId('phone')}
								label={intl.formatMessage(webinarFormMessages.phone)}
								onChange={updateTextValue('phone')}
								type="tel"
								value={values.phone}
							/>
						)}
					</div>
					<div aria-hidden="true" css={visuallyHiddenCss}>
						<label htmlFor={getId('alternateEmail')}>
							{intl.formatMessage(webinarFormMessages.alternateEmail)}
						</label>
						<input
							autoComplete="off"
							id={getId('alternateEmail')}
							name="alternate_email"
							onChange={updateTextValue('alternateEmail')}
							tabIndex={-1}
							type="text"
							value={values.alternateEmail}
						/>
					</div>
					<p css={[disclosureCss, alignedContentCss]}>
						{intl.formatMessage(webinarFormMessages.privacyDisclosure)}
						<a css={privacyLinkCss} href={privacyPolicyHref}>
							{intl.formatMessage(webinarFormMessages.privacyPolicy)}
						</a>
						.
					</p>
					{showMarketingOptIn && (
						<label css={checkboxLabelCss}>
							<input
								checked={values.marketingOptIn}
								css={checkboxCss}
								onChange={(event) =>
									setValues((current) => ({
										...current,
										marketingOptIn: event.target.checked,
									}))
								}
								type="checkbox"
							/>
							<span>{intl.formatMessage(webinarFormMessages.marketingOptIn)}</span>
						</label>
					)}
					{serverError && (
						<div
							css={[alertCss, alignedContentCss]}
							ref={serverErrorRef}
							role="alert"
							tabIndex={-1}
						>
							{intl.formatMessage(webinarFormMessages.submitError)}
						</div>
					)}
					<div css={alignedContentCss}>
						<Button
							isDisabled={isSubmitting}
							isLoading={isSubmitting}
							size="large"
							type="submit"
							variant={buttonVariant}
						>
							{submitLabel}
						</Button>
					</div>
				</form>
			</section>
		</TypographyStyles>
	);
};

const UniformWebinarForm = (props: UniformWebinarFormProps) => (
	<ConsentHubClientConfigProvider>
		<WebinarFormContent {...props} />
	</ConsentHubClientConfigProvider>
);

export default UniformWebinarForm;
